import { SignalObject, signalObject } from "omi";
import merge from "lodash-es/merge";

import { initApi } from "@/utils/api";
import { networkSignal } from "@/utils/signals";
import { DocumentationTheme, Preferences } from "@/api";
import { DaysPerWeek, HoursPerDay } from "@/models";

type Slice = Preferences;

type Actions = {
  getPreferencesDTO: () => Preferences;
  updateCategories: (categories: Preferences["selectedCategories"]) => void;
  updateTimePreferences: (hoursPerDay: HoursPerDay, daysPerWeek: DaysPerWeek) => void;
  updateCurrentProject: (currentProject: Preferences["currentProject"]) => void;
  updatePreferences: (preferences: Partial<Preferences>) => void;
};

export type PreferencesSlice = SignalObject<Slice> & Actions;

export function createPreferencesSlice(preferencesBootstrap: Preferences | undefined): PreferencesSlice {
  const initialState: Slice = {
    selectedCategories: preferencesBootstrap?.selectedCategories || [],
    hoursPerDay: preferencesBootstrap?.hoursPerDay ?? HoursPerDay.ONE,
    daysPerWeek: preferencesBootstrap?.daysPerWeek ?? DaysPerWeek.FIVE,
    currentProject: preferencesBootstrap?.currentProject || "",
    isToolbarPinned: preferencesBootstrap?.isToolbarPinned,
    documentationTheme: preferencesBootstrap?.documentationTheme || DocumentationTheme.LIGHT,
    profileURL: preferencesBootstrap?.profileURL,
  };

  const state = signalObject(initialState);

  return {
    ...state,
    updateCategories: updateCategoriesSelector(state),
    getPreferencesDTO: getPreferencesDTOSelector(state),
    updateTimePreferences: updateTimePreferencesSelector(state),
    updateCurrentProject: updateCurrentProjectSelector(state),
    updatePreferences: updatePreferencesFunc(state),
  };
}

function updatePreferencesFunc(store: SignalObject<Slice>): (preferences: Partial<Preferences>) => void {
  const updatePrefsSignal = networkSignal<Preferences>({
    retry: { delayMs: 500, backoffMultiplier: 2, maxAttempts: 3 },
  });

  const getPreferencesDTO = getPreferencesDTOSelector(store);
  return async (toUpdate: Partial<Preferences>) => {
    const api = initApi();
    const dto = getPreferencesDTO();
    const preferences = merge(dto, toUpdate);

    await updatePrefsSignal.run(async () => await api.setUserPreferences({ preferences }));
  };
}

function updateTimePreferencesSelector(store: SignalObject<Slice>): (hoursPerDay: number, daysPerWeek: number) => void {
  return (hoursPerDay: HoursPerDay, daysPerWeek: DaysPerWeek) => {
    let hours = hoursPerDay;
    let days = daysPerWeek;

    const wasPreviouslyNone =
      store.hoursPerDay.value === HoursPerDay.NONE || store.daysPerWeek.value === DaysPerWeek.NONE;

    if (!wasPreviouslyNone && (hoursPerDay === HoursPerDay.NONE || daysPerWeek === DaysPerWeek.NONE)) {
      hours = HoursPerDay.NONE;
      days = DaysPerWeek.NONE;
    }

    if (wasPreviouslyNone) {
      hours = hours === HoursPerDay.NONE ? HoursPerDay.ONE : hours;
      days = days === DaysPerWeek.NONE ? DaysPerWeek.FIVE : days;
    }

    store.hoursPerDay.value = hours;
    store.daysPerWeek.value = days;
  };
}

function updateCategoriesSelector(store: SignalObject<Slice>): (categories: Preferences["selectedCategories"]) => void {
  return (categories: Preferences["selectedCategories"]) => {
    store.selectedCategories.value = categories;
  };
}

function updateCurrentProjectSelector(
  store: SignalObject<Slice>,
): (currentProject: Preferences["currentProject"]) => void {
  return (currentProject: Preferences["currentProject"]) => {
    store.currentProject.value = currentProject;
  };
}

function getPreferencesDTOSelector(store: SignalObject<Slice>): () => Preferences {
  return () => {
    return {
      selectedCategories: store.selectedCategories.value,
      hoursPerDay: store.hoursPerDay.value,
      daysPerWeek: store.daysPerWeek.value,
      currentProject: store.currentProject.value,
      isToolbarPinned: store.isToolbarPinned?.value,
      documentationTheme: store.documentationTheme?.value,
      profileURL: store.profileURL?.value,
    };
  };
}
